import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Alert, Spinner, Card, Row, Col, InputGroup
} from 'react-bootstrap';
import supabase from '../../utils/supabaseClient';

const SystemModal = ({ 
  show, 
  onHide, 
  modalType = 'create',
  selectedSystem = null,
  onSuccess = () => {}
}) => {
  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    icon: '',
    color: '#007bff',
    route: '',
    active: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ícones populares para sistemas
  const popularIcons = [
    '🏢', '💰', '📊', '👥', '🛒', '📋', '🎯', '📈',
    '⚙️', '🔧', '💼', '📅', '🏥', '🎓', '🚗', '🏠'
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      if (modalType === 'edit' && selectedSystem) {
        setFormData({
          name: selectedSystem.name || '',
          description: selectedSystem.description || '',
          slug: selectedSystem.slug || '',
          icon: selectedSystem.icon || '',
          color: selectedSystem.color || '#007bff',
          route: selectedSystem.route || '',
          active: selectedSystem.active !== false
        });
      } else {
        resetForm();
      }
      setError(null);
    }
  }, [show, modalType, selectedSystem]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      slug: '',
      icon: '',
      color: '#007bff',
      route: '',
      active: true
    });
  };

  // Gerar slug automaticamente baseado no nome
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove hífens duplicados
      .trim();
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      // Só gerar slug automaticamente se estiver criando um novo sistema
      slug: modalType === 'create' ? generateSlug(name) : prev.slug
    }));
  };

  const handleCreateSystem = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validações
      if (!formData.name || !formData.slug) {
        throw new Error('Nome e slug são obrigatórios');
      }

      // Verificar se slug já existe
      const { data: existingSystem, error: checkError } = await supabase
        .from('systems')
        .select('id')
        .eq('slug', formData.slug)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingSystem) {
        throw new Error('Já existe um sistema com este slug');
      }

      // Criar sistema
      const { error: insertError } = await supabase
        .from('systems')
        .insert({
          name: formData.name,
          description: formData.description,
          slug: formData.slug,
          icon: formData.icon,
          color: formData.color,
          route: formData.route,
          active: formData.active
        });

      if (insertError) throw insertError;

      onSuccess(`Sistema "${formData.name}" criado com sucesso!`);
      onHide();

    } catch (error) {
      console.error('Erro ao criar sistema:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSystem = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name || !formData.slug) {
        throw new Error('Nome e slug são obrigatórios');
      }

      // Atualizar sistema
      const { error: updateError } = await supabase
        .from('systems')
        .update({
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          route: formData.route,
          active: formData.active
        })
        .eq('id', selectedSystem.id);

      if (updateError) throw updateError;

      onSuccess(`Sistema "${formData.name}" atualizado com sucesso!`);
      onHide();

    } catch (error) {
      console.error('Erro ao atualizar sistema:', error);
      setError('Erro ao atualizar sistema: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.name && formData.slug;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {modalType === 'create' ? 'Novo Sistema' : 'Editar Sistema'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}
        
        <Row className="g-4">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Nome do Sistema *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex: Sistema de Vendas"
              />
            </Form.Group>
          </Col>
          
          <Col md={4}>
            <Form.Group>
              <Form.Label>Ícone</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    icon: e.target.value
                  }))}
                  placeholder="🔧"
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}
                >
                  Limpar
                </Button>
              </InputGroup>
              </Form.Group>
        
              
              {/* Ícones populares */}
              <div className="mt-2">
                <small className="text-muted">Ícones populares:</small>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {popularIcons.map(icon => (
                    <Button
                      key={icon}
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, icon }))}
                      style={{ fontSize: '1.2em', padding: '0.25rem 0.5rem' }}
                    >
                      {icon}
                    </Button>
                  ))}
                </div>
              </div>
            </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Form.Group>
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                placeholder="Breve descrição do sistema"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Slug *</Form.Label>
              <Form.Control
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  slug: e.target.value
                }))}
                placeholder="sistema-vendas"
                disabled={modalType === 'edit'}
              />
              <Form.Text className="text-muted">
                Identificador único usado nas URLs (sem espaços ou caracteres especiais)
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={4}>
            <Form.Group>
              <Form.Label>Cor do Tema</Form.Label>
              <Form.Control
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  color: e.target.value
                }))}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Form.Group>
              <Form.Label>URL/Rota</Form.Label>
              <Form.Control
                type="text"
                value={formData.route}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  route: e.target.value
                }))}
                placeholder="https://vendas.empresa.com ou /vendas"
              />
              <Form.Text className="text-muted">
                URL externa (https://) ou rota interna (/rota). Deixe em branco para "Em desenvolvimento"
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Form.Check
              type="switch"
              label={formData.active ? "Sistema Ativo" : "Sistema Inativo"}
              checked={formData.active}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                active: e.target.checked
              }))}
            />
          </Col>
        </Row>

        {/* Preview */}
        <Row className="mt-4">
          <Col>
            <h6>Preview:</h6>
            <Card className="border-light bg-light">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <span className="me-3" style={{ fontSize: '2em' }}>
                    {formData.icon || '🔧'}
                  </span>
                  <div>
                    <h5 className="mb-1" style={{ color: formData.color }}>
                      {formData.name || 'Nome do Sistema'}
                    </h5>
                    <p className="mb-0 text-muted">
                      {formData.description || 'Descrição do sistema'}
                    </p>
                    <small className="text-muted">
                      Slug: {formData.slug || 'slug-do-sistema'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={modalType === 'create' ? handleCreateSystem : handleUpdateSystem}
          disabled={loading || !isFormValid()}
        >
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          {modalType === 'create' ? 'Criar Sistema' : 'Atualizar Sistema'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SystemModal;